import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { BigButtonOutlined } from '../components/Buttons/buttons.styles';
import FieldzoneModal from '../components/FieldzoneModal';
import Publicity from '../components/Publicity';
import Webloyalty from '../components/WebLoyalty';
import { Environment } from '../constants/environments';
import { EventTrackLabel } from '../constants/ga4';
import { ImagesApp } from '../constants/images';
import { useCartContext } from '../hooks/contexts/cart.context';
import { useEventContext } from '../hooks/contexts/event.context';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useModalContext } from '../hooks/contexts/modal.context';
import { useUserContext } from '../hooks/contexts/user.context';
import useGuardian from '../hooks/useGuardian';
import { useMetaConversions } from '../hooks/useMetaConversions';
import * as Styles from '../styles/pages//styles.payment-confirmed';
import FacebookPixel from '../trackings/FacebookPixel';
import GoogleAnalytics from '../trackings/GoogleAnalytics';
import formatDate from '../utils/formateDate';

const PaymentConfirmed = () => {
  const posthog = usePostHog();
  const [eventDate, setEventDate] = useState<string | null>(null);
  const { render } = useGuardian();
  const { translate } = useI18nContext();
  const { event, couponApplied } = useEventContext();
  const { cart, summaryData, transactionId, combosInCart, resetCart } = useCartContext();
  const { user } = useUserContext();
  const { setContentModal, toggleModal } = useModalContext();
  const {
    supportedLocales,
    currentLocale : { locale },
  } = useI18nContext();
  const { clientUrl } = useParams();

  const handleAnalyticsTracking = () => {
    // Prevent analytics tracking if summaryData is empty
    if (!summaryData || summaryData.length === 0) {
      console.warn('Cannot track analytics: summaryData is empty');
      return;
    }

    posthog.capture('purchase:success_page_view');
    const contentsToFbPixelTrack = summaryData.map((ticket) => ({
      id : String(ticket.id),
      quantity : ticket.quantity,
    }));

    const valueToFbPixelTrackWithTax = summaryData.reduce(
      (acc, ticket) => acc + (ticket.price + ticket.tax) * ticket.quantity,
      0
    );

    FacebookPixel.trackPurchase(
      valueToFbPixelTrackWithTax, contentsToFbPixelTrack, String(event?.id), String(event?.accepted_currencies)
    );

    const totalTax = summaryData.reduce((acc, ticket) => acc + ticket.tax, 0);
    GoogleAnalytics.trackEvent(
      EventTrackLabel.PURCHASE,
      {
        currency : String(event?.accepted_currencies),
        affiliation : String(clientUrl),
        transaction_id : transactionId,
        tax : totalTax,
        shipping : 0,
        items : summaryData.map((ticket) => ({
          item_id : String(event?.id),
          item_name : String(event?.title),
          coupon : couponApplied ? couponApplied.coupon.name : '',
          item_category : ticket.date,
          item_brand : ticket.group,
          item_variant : ticket.title,
          quantity : ticket.quantity,
          discount : couponApplied ? couponApplied.coupon.value_discount : 0,
          price : ticket.price,
        })),
        value : summaryData.reduce(
          (acc, ticket) => acc + ticket.accumulatedValueInCart,
          0
        ),
      },
    );
  };

  const showFieldzoneModal = () => {
    setContentModal(<FieldzoneModal />);
    toggleModal({ open : true });
  };

  // Track Meta Conversions API PageView automatically
  useMetaConversions();

  useEffect(() => {

    // Capture event date before reset
    const capturedEventDate = cart?.[0]?.type?.[0]?.dates?.[0]?.datetime ||
      combosInCart?.[0]?.comboDates?.[0]?.datetime;

    if (capturedEventDate) {
      setEventDate(capturedEventDate);
    }

    // Handle analytics tracking first (needs summaryData)
    handleAnalyticsTracking();

    // Show Fielzone modal for events with "Fielzone" category
    const hasFieldzoneCategory = event?.categories?.some(
      (category) => category.name?.toLowerCase().includes('fielzone')
    );

    if (event && hasFieldzoneCategory) {
      showFieldzoneModal();
    }

    // Reset cart after analytics to prevent duplicate transactions when user navigates back
    // Preserve transactionId to maintain duplicate transaction prevention
    resetCart(true);
  }, [event]);

  if (!event || !cart || !user) return null;

  return render(
    <Styles.Wrapper>
      {event.accepted_currencies === 'BRL' &&
        event.producerId !== 12049153 &&
        <Webloyalty hash="a5dfc9a285babcae6665bc7bfcff92cb"
        />}
      <Styles.Aside>
        <Styles.SquareWhite />
        <Styles.SquareImageEvent>
          <Styles.ImageEvent src={event.poster.large} />
        </Styles.SquareImageEvent>
      </Styles.Aside>

      <Styles.Main>
        <Styles.Container>
          <Styles.Subtitle>
            {translate(['INFORMATION', 'PAYMENT_CONFIRMED_TITLE'])}
          </Styles.Subtitle>
          <div>
            <Styles.EventTitle>
              {translate(['INFORMATION', 'AVAILABLE_SOON'])}
            </Styles.EventTitle>
            <Styles.Separator />
          </div>
          <Styles.EventTitle>
            {event.title}
          </Styles.EventTitle>
          <Styles.ContainerLocation>
            <img src={ImagesApp.svg.icLocation} alt="" />
            <Styles.Location>
              {`${event.place.name}, ${event.place.city} - ${event.place.state}`}
            </Styles.Location>
          </Styles.ContainerLocation>
          <Styles.TitleEventDate>
            {translate(['INFORMATION', 'EVENT_DATES'])}
          </Styles.TitleEventDate>
          <Styles.EventDate>
            {eventDate ? formatDate({
              dateString : eventDate,
              locale,
              supportedLocales,
            }) : (translate(['INFORMATION', 'DATE_NOT_AVAILABLE']) || 'Data não disponível')}
          </Styles.EventDate>
          <Styles.DetailsSendEmail>
            {translate(['INFORMATION', 'PURCHASE_DETAILS_SENT_TO'])}
          </Styles.DetailsSendEmail>
          <Styles.EmailText>{user.email}</Styles.EmailText>

          <Publicity />
        </Styles.Container>

        <Styles.ContainerButtons>
          <BigButtonOutlined
            onClick={() => {
              posthog.capture('purchase:success_page_show_events_click');
              window.location.href = Environment.SITE_URL;
            }}
          >
            {translate(['INFORMATION', 'SEE_MORE_EVENTS'])}
          </BigButtonOutlined>
        </Styles.ContainerButtons>
      </Styles.Main>
    </Styles.Wrapper>
  );
};

export default PaymentConfirmed;
